<template>
    <div v-if="stopTime" class="stop-time d-flex flex-column justify-center" :class="cssClasses">
        <div v-if="isCancelled && !hideCancelledLabel" class="stop-time__cancelled-label text-error">
            {{ t('common.resources.cancelled', 'Cancelled') }}
        </div>
        <div class="d-flex align-center justify-end">
            <realtime-indicator v-if="!isCancelled && realtime" class="mr-1" />
            <div class="stop-time__title" :class="titleCssClasses">
                {{ stopTime }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useDurationHelpers } from '@/features/common/composables';
import { RealtimeIndicator } from '@/features/common/components';
import { toLocaleTimeString } from '@geta/kolumbus-frontend/composables';
import { differenceInSeconds, isDate } from 'date-fns';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

// minimum diff, in seconds, below which the departure time will be displayed as relative time, and above which it will be displayed as absolute time.
const minDiffForRelativeTime = 1200;

interface Props {
    /**
     * The time to display. If `now` is also provided, and the difference between `modelValue` and `now` is less than `minDiffForRelativeTime`,
     * the time will be displayed as relative time (e.g. "in 5 min").
     */
    modelValue?: Date | null;

    /**
     * Whether the stop time is real-time.
     */
    realtime?: boolean;

    /**
     * Whether the stop time is cancelled.
     */
    isCancelled?: boolean;

    /**
     * The current time. Used for calculating relative time.
     */
    now?: Date;

    /**
     * Whether to hide the "Cancelled" label.
     */
    hideCancelledLabel?: boolean;
}

const { t } = useI18n();
const { humanizeDuration } = useDurationHelpers();
const props = defineProps<Props>();
const cssClasses = computed(() => ({ 'stop-time--cancelled': props.isCancelled }));
const titleCssClasses = computed(() => ({
    'text-decoration-line-through opacity-60': props.isCancelled
}));
const stopTime = computed(() => {
    if (!props.modelValue || !isDate(props.modelValue)) return '';

    if (isDate(props.now)) {
        const diffForRelativeTime = differenceInSeconds(props.modelValue, props.now as Date);
        if (diffForRelativeTime < minDiffForRelativeTime) {
            return humanizeDuration(diffForRelativeTime);
        }
    }

    return toLocaleTimeString(props.modelValue);
});
</script>

<style lang="scss">
@use 'vuetify/settings' as *;

.stop-time {
    &__cancelled-label {
        font-size: calc(18px / $font-size-root) * 1rem;
        font-weight: 500;
    }
}
</style>
